import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  Inject,
  inject,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional
} from '@angular/core';
import { ListItemRendererComponent, PimTemplateProperties } from 'gung-list';
import { first, forkJoin, of, Subject, Subscription, switchMap } from 'rxjs';
import { Availability } from '../../models/availability';
import { CustomerProductPrice } from '../../models/price';
import { Product, ProductExtended } from '../../models/product';
import { AssortmentService } from '../../services/assortment.service';
import { AuthService } from '../../services/auth/auth.service';
import { GungFlowService } from '../../services/gung-flow/gung-flow.service';
import { PriceService } from '../../services/price/price.service';
import { SelectedCustomerService } from '../../services/selected-customer/selected-customer.service';
import { GungFlow } from '../../state/flow/types';
import { GridViewProductCardV2 } from '../product-grid-view-v2/product-grid-view-v2.component';
import { AvailabilityService } from '../../services/availability/availability.service';
import { PriceConfigService } from '../../services/price-config/price-config.service';
import { NavigationConfig } from '../../models/navigation-config';

@Component({
  selector: 'lib-product-list-view-v2',
  templateUrl: './product-list-view-v2.component.html',
  styleUrls: ['./product-list-view-v2.component.scss'],
  standalone: false
})
export class ProductListViewV2Component extends ListItemRendererComponent<Product[]> implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  // @ViewChildren(CustomHostDirective)
  // private viewChildren: QueryList<CustomHostDirective>;

  // public listItemRenderer: Type<ListItemRendererComponent<GridViewProductCardV2>>;

  protected keyedMapData: { [id: string]: GridViewProductCardV2 } = {};
  protected subscriptions: Subscription[] = [];
  protected flowSubscriptions: Subscription;

  public includeAvailability = false;
  public isSales = false;
  public isAnonymous = true;
  public mappedData: GridViewProductCardV2[] = [];
  protected currentAssortment;

  protected mappedDataChanged: Subject<void> = new Subject<void>();
  protected unsubscribe: Subject<void> = new Subject();

  protected currentFlow: GungFlow;
  protected oldFlow: GungFlow;
  renderedIds: { [s: string]: string } = {};
  errorMessage = 'ERROR: ';
  findError = false;
  // private componentFactory: ComponentFactory<ListItemRendererComponent<GridViewProductCardV2>>;
  isStandardErp = false;

  private viewInitiated = false;
  isLoading = true;
  // Skips the loadingChange emission for the very first render, since that's already covered by the list's own initial loading state.
  protected hasLoadedOnce = false;

  protected priceService = inject(PriceService);
  protected gungFlowService = inject(GungFlowService);
  protected authService = inject(AuthService);
  protected componentFactoryResolver = inject(ComponentFactoryResolver);
  protected changeDetectorRef = inject(ChangeDetectorRef);
  protected assortmentService = inject(AssortmentService);
  protected selectedCustomerService = inject(SelectedCustomerService);
  protected availabilityService = inject(AvailabilityService);
  protected priceConfigService = inject(PriceConfigService);

  constructor(
    @Optional()
    @Inject('environment')
    public environment: NavigationConfig
  ) {
    super();
  }

  public ngOnInit() {
    this.authService
      .getRoles()
      .pipe(first())
      .subscribe(roles => {
        this.isSales =
          roles.filter(role => role.toUpperCase() === 'ADMIN' || role.toUpperCase() === 'SALES').length > 0;
        this.isAnonymous = roles.filter(role => role.toUpperCase() === 'ANONYMOUS').length > 0;
      });

    this.subscribeToProducts();
    this.flowSubscriptions = this.gungFlowService.getSelectedFlow().subscribe(flow => (this.currentFlow = flow));
    this.mappedDataChanged.subscribe(res => {
      // this.renderItemComponents();
    });
  }

  ngAfterViewInit(): void {
    //this.renderFinished?.emit();
  }

  public ngOnChanges() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscribeToProducts();
  }

  public ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.flowSubscriptions.unsubscribe();

    this.mappedDataChanged.complete();

    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  protected subscribeToProducts() {
    if (!this.oldFlow || !this.currentFlow || this.oldFlow.id !== this.currentFlow.id) {
      // remove cached data;
      this.keyedMapData = {};
      this.oldFlow = this.currentFlow;
    }
    const newIds = this.data.map(p => p.id).filter(id => !Object.keys(this.keyedMapData).includes(id));
    if (newIds.length === 0) {
      this.readMappedDataFromCache();
      return;
    }

    this.isLoading = true;
    this.emitLoadingChange(true);

    const subscription = forkJoin({
      productData: of(this.data),
      prices: this.priceService.getCurrentCustomerPrices(newIds).pipe(first()),
      flow: this.gungFlowService.getSelectedFlow().pipe(first())
    }).pipe(
      first(),
      switchMap(({ productData, prices, flow }) => {
        let avs = [];
        this.includeAvailability = flow.useAvailabilities || flow.requireAvailability;
        if (this.includeAvailability) {
          avs = newIds.map(id => this.getProductAvailability(this.data.find(product => product.id === id)));
        }

        if (avs.length > 0) {
          return forkJoin({
            productData: of(productData),
            prices: of(prices),
            avs: of(avs)
          });
        } else {
          return forkJoin({
            productData: of(productData),
            prices: of(prices),
            avs: this.availabilityService.getAvailabilities(newIds).pipe(first())
          });
        }
      })
    ).subscribe(({ productData, prices, avs }) => {
      this.keyedMapData = {
        ...this.keyedMapData,
        ...newIds.reduce((acc, curr) => {
          const product = productData.filter(p => p && p.id === curr)[0];

          if (!product) {
            this.findError = true;
            this.errorMessage += 'No product found. ';
            throw new Error('No product found');
          }

          const price = prices.filter(p => p && p.productId === curr)[0];
          if (!price) {
            this.findError = true;
            this.errorMessage += 'No price found.';
            throw new Error('No price found');
          }

          const av = avs.filter(p => p && p.productId === curr)[0];
          if (!av && this.includeAvailability) {
            this.findError = true;
            this.errorMessage += 'No availability found. ';
            throw new Error('No availability found');
          }

          const item: GridViewProductCardV2 = this.mapItem(curr, product, price, av);
          return {
            ...acc,
            [curr]: item
          };
        }, {})
      };
      this.readMappedDataFromCache();
    });

    this.subscriptions.push(subscription);
  }

  protected getProductAvailability(product: Product): Availability {
    if (product.extra.availabilities) {
      const availabilities = Object.keys(product.extra.availabilities).map(key => product.extra.availabilities[key]);
      if (availabilities.length > 0) {
        return availabilities[0];
      }
    }

    return null;
  }

  protected readMappedDataFromCache() {
    const ids = this.data.map(d => d.id);
    this.mappedData = ids.map(id => this.keyedMapData[id]);

    this.mappedDataChanged.next();
    this.isLoading = false;
    this.changeDetectorRef.detectChanges();
    this.renderFinished?.emit();
    this.emitLoadingChange(false);
  }

  protected emitLoadingChange(loading: boolean): void {
    if (this.hasLoadedOnce) {
      this.loadingChange?.emit(loading);
    }
    if (!loading) {
      this.hasLoadedOnce = true;
    }
  }

  protected mapItem(
    id: string,
    product: ProductExtended,
    price: CustomerProductPrice,
    availability: Availability
  ): GridViewProductCardV2 {
    const isSales = this.isSales;
    const isAnonymous = this.isAnonymous;
    const includeAvailability = this.includeAvailability;

    if (product.extra.gme) {
      this.isStandardErp = true;
    }

    const dynamicCollumnsValues = [];
    if (this.dynamicColumns) {
      for (const [index, column] of this.dynamicColumns.entries()) {
        if (column.isDisplayList) {
          dynamicCollumnsValues.push(product?.dynamicCollumnsValues?.[index]);
        }
      }
    }

    product = {
      ...product,
      dynamicCollumnsValues
    }

    return {
      product,
      price,
      availability,
      isSales,
      isAnonymous,
      includeAvailability
    };
  }

  public trackByFn(index: number, item: any) {
    if (!!item.product) {
      return item.product.id;
    } else {
      return index;
    }
  }

  get dynamicColumnsForList(): PimTemplateProperties[] {
    return this.dynamicColumns?.filter(c => c.isDisplayList) || [];
  }

  getDisplayName(headerKey: PimTemplateProperties){
    let displayName = !!headerKey.displayName && headerKey.displayName != "" ? headerKey.displayName : headerKey.translationKey;

    return displayName;
  }
}
